import { DUNGEON_PLACES, OVERWORLD, placeAt } from './places.js';
import type { ClassId, Dimension, Rarity, Slot, Vec } from './types.js';
export const VERSION = '1.5.0';
export const TICK_RATE = 20;
export const MAX_PLAYERS = 48;
export const MAX_LEVEL = 30;
/** The highest gear tier, which the outer ring's places drop and tempering reaches. */
export const MAX_TIER = 8;
export const MAX_INVENTORY = 18;
export const MAX_VAULT = 48;
export const HAVEN = { x: 0, z: 20, radius: 12 };
/** Trouble at the gate: two creatures just outside the sanctuary edge, visible from spawn. */
export const GATE = { x: 0, z: 3 };
export const GATE_SPAWNS = [
  { kind: 'cinderling', x: -3, z: 5 },
  { kind: 'cinderling', x: 3, z: 4 },
] as const;
export const CLASSES: Record<
  ClassId,
  {
    name: string;
    title: string;
    description: string;
    color: string;
    hp: number;
    mp: number;
    speed: number;
    damage: number;
    rate: number;
    range: number;
    ability: string;
    abilityDescription: string;
    abilityCost: number;
    abilityCooldown: number;
    weapon: string;
    icon: string;
    difficulty: string;
  }
> = {
  arcanist: {
    name: 'Arcanist',
    title: 'THE LIGHT BETWEEN',
    description: 'Turn the old world’s last magic into a storm. Fragile, brilliant, devastating.',
    color: '#9ae2dc',
    hp: 180,
    mp: 120,
    speed: 9,
    damage: 24,
    rate: 0.2,
    range: 26,
    ability: 'Astral nova',
    abilityDescription: 'Unleash 24 piercing bolts. Clears nearby enemy projectiles.',
    abilityCost: 35,
    abilityCooldown: 4.5,
    weapon: 'staff',
    icon: 'spark',
    difficulty: 'Ranged · Area damage',
  },
  ranger: {
    name: 'Wayfarer',
    title: 'FOLLOW THE WIND',
    description: 'A steady bow and a second chance. Slip between the chaos, then strike true.',
    color: '#c8d98b',
    hp: 210,
    mp: 90,
    speed: 10.5,
    damage: 19,
    rate: 0.15,
    range: 30,
    ability: 'Thorn volley',
    abilityDescription: 'Loose a piercing fan of 9 arrows and gain a moment of invulnerability.',
    abilityCost: 28,
    abilityCooldown: 3.5,
    weapon: 'bow',
    icon: 'bow',
    difficulty: 'Long range · Mobility',
  },
  sentinel: {
    name: 'Sentinel',
    title: 'STAND AGAINST THE ASH',
    description:
      'An oath clad in old iron. Carry a shield into the storm and leave a path for others.',
    color: '#edb879',
    hp: 300,
    mp: 85,
    speed: 8.5,
    damage: 36,
    rate: 0.25,
    range: 15,
    ability: 'Dawn ward',
    abilityDescription:
      'Heal nearby allies, erase close projectiles, and become invulnerable for 1.5 seconds.',
    abilityCost: 32,
    abilityCooldown: 5,
    weapon: 'blade',
    icon: 'shield',
    difficulty: 'Mid range · Protection',
  },
};
export const RARITIES: Record<Rarity, { label: string; color: string; mult: number }> = {
  common: { label: 'Weathered', color: '#acb5aa', mult: 1 },
  uncommon: { label: 'Verdant', color: '#a9d28d', mult: 1.4 },
  rare: { label: 'Astral', color: '#a8bcff', mult: 1.95 },
  relic: { label: 'Relic', color: '#ecc087', mult: 2.8 },
};
/** Derived view of the overworld places, kept for existing callers and saved tests. */
export const ZONES = OVERWORLD.map((p) => ({
  id: p.id,
  name: p.name,
  subtitle: p.subtitle,
  x: p.x,
  z: p.z,
  radius: p.radius,
  tier: p.tier,
  color: p.color,
}));
/** Derived view of the dungeon places, in their established key order. */
export const DUNGEONS = Object.fromEntries(
  DUNGEON_PLACES.map((p) => [
    p.dimension!,
    {
      name: p.name,
      x: p.x,
      z: p.z,
      color: p.color,
      level: p.levels[0],
      description: p.subtitle,
    },
  ]),
) as Record<
  Exclude<Dimension, 'wilds'>,
  {
    name: string;
    x: number;
    z: number;
    color: string;
    level: number;
    description: string;
  }
>;
export const LANDMARKS = [
  { id: 'vault', name: 'Memory vault', x: 6, z: 22, icon: 'chest' },
  { id: 'forge', name: 'The Cinder Smith', x: -6, z: 22, icon: 'anvil' },
  { id: 'hollow', ...DUNGEONS.hollow, icon: 'portal' },
  { id: 'crucible', ...DUNGEONS.crucible, icon: 'portal' },
  { id: 'eclipse', ...DUNGEONS.eclipse, icon: 'portal' },
];
export const QUESTS = [
  {
    name: 'A spark in the ashes',
    text: 'Defeat 6 creatures at the gate.',
    target: 6,
    kind: 'kill',
    gold: 35,
    xp: 100,
    hint: 'Trouble waits just north of the Hearth. Your third kill drops a better weapon.',
  },
  {
    name: 'Old roots, new courage',
    text: 'Break a realm seal or defeat the Sovereign.',
    target: 1,
    kind: 'warden',
    gold: 100,
    xp: 250,
    hint: 'Wardens wait in the Grove, Glasswaste, and northern meadow.',
  },
  {
    name: 'What the water kept',
    text: 'Defeat the keeper of the Sunken Archive.',
    target: 1,
    kind: 'archivist',
    gold: 150,
    xp: 450,
    hint: 'The teal portal lies west of the Hearth. Level 5 recommended.',
  },
  {
    name: 'At the heart of the fire',
    text: 'Defeat the Forgemother in the Ember Crucible.',
    target: 1,
    kind: 'forgemother',
    gold: 220,
    xp: 700,
    hint: 'Find the amber portal east of the Hearth. Level 10 recommended.',
  },
  {
    name: 'Keep the fire alive',
    text: 'Defeat the Ashen Sovereign at the Crown.',
    target: 1,
    kind: 'sovereign',
    gold: 500,
    xp: 1500,
    hint: 'Defeat all three realm wardens to break the Sovereign’s seal.',
  },
  {
    name: 'The world is still yours',
    text: 'Reach level 20 and prepare for the Elder Convergence.',
    target: 20,
    kind: 'level',
    gold: 500,
    xp: 0,
    hint: 'The violet portal south of the Hearth awakens after your first Sovereign victory.',
  },
  {
    name: 'Beyond the last horizon',
    text: 'Defeat Vesper and complete the Elder Convergence.',
    target: 1,
    kind: 'nullelder',
    gold: 700,
    xp: 0,
    hint: 'Three elders. Five encounters. Return to each lit altar to advance.',
  },
  {
    name: 'A deeper kind of dawn',
    text: 'Complete Elder depth 3.',
    target: 3,
    kind: 'depth',
    gold: 900,
    xp: 0,
    hint: 'Choose a depth in your journey. Each clear unlocks the next, up to 12.',
  },
];
/**
 * How a creature behaves between shots. The pattern stays one per creature so the bullets
 * remain readable; the behaviour is where the mastery lives, because it decides which
 * creature in a mixed pack you have to kill first.
 */
export type Behaviour =
  'charger' | 'kiter' | 'bulwark' | 'splitter' | 'summoner' | 'anchor' | 'lantern';
export interface EnemyDef {
  name: string;
  hp: number;
  damage: number;
  speed: number;
  radius: number;
  range: number;
  rate: number;
  xp: number;
  tier: number;
  pattern: 'aim' | 'fan' | 'ring' | 'spiral' | 'burst';
  color: string;
  boss?: boolean;
  elder?: boolean;
  behaviour?: Behaviour;
  /** What a splitter leaves behind when it dies. */
  splits?: { kind: string; count: number };
  /** What a summoner calls, how many it keeps alive, and how long between calls. */
  summons?: { kind: string; count: number; cooldown: number };
  /** Half-width of a bulwark's shielded front arc, in radians. Flank it or lose the damage. */
  guard?: number;
  /** A lantern's light: allies inside it fire faster, and a visible line says who. */
  aura?: { radius: number; rate: number };
  /** The range a kiter tries to hold, and the distance a charger commits from. */
  prefer?: number;
  /** How much of its own health a boss must lose during one windup for the attack to break. */
  breakPoint?: number;
  /** A boss's exposed core: shots landing outside this front arc hit it for more. */
  weak?: { arc: number; multiplier: number };
  /** Projectile family. Kept beside the creature so a new kind never edits the realm. */
  style?: number;
  /** The place this creature belongs to, for the bestiary and the atlas. */
  home?: string;
  lore: string;
}
export const ENEMIES: Record<string, EnemyDef> = {
  tideelder: {
    name: 'Thalassa, Elder of Tides',
    hp: 18000,
    damage: 38,
    speed: 0.7,
    radius: 2.6,
    range: 38,
    rate: 1.8,
    xp: 2200,
    tier: 6,
    pattern: 'ring',
    color: '#8fe3ef',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    elder: true,
    style: 9,
    lore: 'She remembers the ocean before there were shores. Every tide is an attempt to bring it back.',
  },
  cinderelder: {
    name: 'Pyra, Elder of Cinders',
    hp: 22000,
    damage: 42,
    speed: 0.8,
    radius: 2.8,
    range: 38,
    rate: 1.85,
    xp: 2600,
    tier: 6,
    pattern: 'burst',
    color: '#ffbd81',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    elder: true,
    style: 8,
    lore: 'The Forgemother borrowed her flame. Pyra has crossed the dark to reclaim it.',
  },
  nullelder: {
    name: 'Vesper, Elder of Silence',
    hp: 28000,
    damage: 44,
    speed: 0.6,
    radius: 3,
    range: 40,
    rate: 1.8,
    xp: 3200,
    tier: 6,
    pattern: 'spiral',
    color: '#d0b4ff',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    elder: true,
    style: 10,
    lore: 'In the space between stars, something listened. Now it has learned to answer.',
  },
  cinderling: {
    name: 'Cinderling',
    hp: 56,
    damage: 14,
    speed: 2.2,
    radius: 0.65,
    range: 12,
    rate: 2.5,
    xp: 24,
    tier: 1,
    pattern: 'aim',
    color: '#e9aa70',
    style: 4,
    lore: 'A coal that forgot how to go out. It follows the warmth of living things.',
  },
  thornling: {
    name: 'Thornling',
    hp: 82,
    damage: 16,
    speed: 1.4,
    radius: 0.8,
    range: 17,
    rate: 2.8,
    xp: 32,
    tier: 1,
    pattern: 'fan',
    color: '#a8bf7b',
    style: 5,
    lore: 'The orchard’s last guardians, still defending trees that burned long ago.',
  },
  wisp: {
    name: 'Hollow Wisp',
    hp: 145,
    damage: 23,
    speed: 2.7,
    radius: 0.6,
    range: 20,
    rate: 2.3,
    xp: 42,
    tier: 2,
    pattern: 'spiral',
    color: '#91d8d2',
    style: 7,
    lore: 'Unfinished thoughts, set adrift when the Archive sank.',
  },
  scarab: {
    name: 'Glass Scarab',
    hp: 200,
    damage: 28,
    speed: 2,
    radius: 0.85,
    range: 20,
    rate: 2.3,
    xp: 58,
    tier: 3,
    pattern: 'fan',
    color: '#e2bc8a',
    style: 6,
    lore: 'Their mirrored shells contain the sunrise from the day the world ended.',
  },
  watcher: {
    name: 'Ash Watcher',
    hp: 300,
    damage: 32,
    speed: 1.5,
    radius: 0.9,
    range: 22,
    rate: 2.8,
    xp: 85,
    tier: 4,
    pattern: 'ring',
    color: '#c0a4d7',
    style: 7,
    lore: 'They kept their vigil through the fire. No one told them the war was over.',
  },
  rootwarden: {
    name: 'The Rootbound',
    hp: 3200,
    damage: 25,
    speed: 1.1,
    radius: 1.9,
    range: 25,
    rate: 1.9,
    xp: 350,
    tier: 2,
    pattern: 'ring',
    color: '#afce92',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    style: 5,
    lore: 'The oldest tree gave up its roots to search for rain. It has found only ash.',
  },
  glasswarden: {
    name: 'The Glass Colossus',
    hp: 5100,
    damage: 32,
    speed: 1.2,
    radius: 2,
    range: 26,
    rate: 1.7,
    xp: 500,
    tier: 3,
    pattern: 'fan',
    color: '#efbd7f',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    style: 6,
    lore: 'A giant cast in the heat of a dying star. Every step grinds a century to dust.',
  },
  duskwarden: {
    name: 'The Dusk Herald',
    hp: 4000,
    damage: 28,
    speed: 1.5,
    radius: 1.7,
    range: 26,
    rate: 1.6,
    xp: 450,
    tier: 2,
    pattern: 'spiral',
    color: '#b6a0d1',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    style: 7,
    lore: 'It once rang the bells for evening prayers. Now it announces the end of all things.',
  },
  archivist: {
    name: 'The Drowned Archivist',
    hp: 8000,
    damage: 28,
    speed: 0.8,
    radius: 2,
    range: 32,
    rate: 1.4,
    xp: 900,
    tier: 3,
    pattern: 'spiral',
    color: '#8ae1dd',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    style: 7,
    lore: 'Every book was saved. Every reader was lost. The keeper sees no difference now.',
  },
  forgemother: {
    name: 'The Forgemother',
    hp: 12500,
    damage: 36,
    speed: 1,
    radius: 2.3,
    range: 34,
    rate: 1.5,
    xp: 1400,
    tier: 4,
    pattern: 'burst',
    color: '#f0ad79',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    style: 4,
    lore: 'She forged a sun to keep her children warm. She is still trying to mend it.',
  },
  sovereign: {
    name: 'The Ashen Sovereign',
    hp: 26000,
    damage: 42,
    speed: 0.9,
    radius: 2.7,
    range: 38,
    rate: 1.25,
    xp: 3000,
    tier: 5,
    pattern: 'spiral',
    color: '#f0c991',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    style: 8,
    lore: 'The first to carry the flame. The last to let it go. A crown is just another kind of kindling.',
  },
  brineclaw: {
    name: 'Brineclaw',
    hp: 340,
    damage: 30,
    speed: 2.6,
    radius: 0.85,
    range: 14,
    rate: 2.4,
    xp: 95,
    tier: 4,
    pattern: 'aim',
    color: '#7fbfc0',
    behaviour: 'charger',
    prefer: 9,
    style: 4,
    home: 'coast',
    lore: 'It walked out of the water the day the water arrived, and it has not stopped walking.',
  },
  tidewisp: {
    name: 'Tide Wisp',
    hp: 290,
    damage: 28,
    speed: 2.4,
    radius: 0.65,
    range: 24,
    rate: 2.6,
    xp: 100,
    tier: 4,
    pattern: 'ring',
    color: '#9fd9d4',
    behaviour: 'kiter',
    prefer: 18,
    style: 7,
    home: 'coast',
    lore: 'A drowned lantern-keeper’s last idea, still circling the place the light used to be.',
  },
  stonebark: {
    name: 'Stonebark',
    hp: 620,
    damage: 34,
    speed: 1.1,
    radius: 1,
    range: 18,
    rate: 2.6,
    xp: 135,
    tier: 4,
    pattern: 'fan',
    color: '#a29b7c',
    behaviour: 'bulwark',
    guard: 1.1,
    style: 5,
    home: 'orchard',
    lore: 'It turned its back to the fire to shelter the saplings. The fire never came; the cold did.',
  },
  thornmother: {
    name: 'Thornmother',
    hp: 560,
    damage: 32,
    speed: 1.2,
    radius: 0.95,
    range: 22,
    rate: 2.8,
    xp: 150,
    tier: 5,
    pattern: 'spiral',
    color: '#b3a86f',
    behaviour: 'summoner',
    summons: { kind: 'thornling', count: 2, cooldown: 14 },
    style: 5,
    home: 'orchard',
    lore: 'Every orchard needs a keeper. This one keeps sending children out to look for spring.',
  },
  saltstrider: {
    name: 'Salt Strider',
    hp: 700,
    damage: 36,
    speed: 2.9,
    radius: 0.8,
    range: 26,
    rate: 2.4,
    xp: 175,
    tier: 5,
    pattern: 'fan',
    color: '#e2d6b4',
    behaviour: 'kiter',
    prefer: 21,
    style: 6,
    home: 'saltflat',
    lore: 'Long legs for a long country. It has crossed the flat so many times it no longer stops.',
  },
  mirage: {
    name: 'Mirage Husk',
    hp: 640,
    damage: 34,
    speed: 2.2,
    radius: 0.9,
    range: 18,
    rate: 2.5,
    xp: 165,
    tier: 5,
    pattern: 'aim',
    color: '#d8c69c',
    behaviour: 'splitter',
    splits: { kind: 'miragesliver', count: 2 },
    style: 4,
    home: 'saltflat',
    lore: 'Whatever it was made of, there was never quite enough of it to be one thing.',
  },
  miragesliver: {
    name: 'Mirage Sliver',
    hp: 190,
    damage: 24,
    speed: 3.1,
    radius: 0.5,
    range: 14,
    rate: 2.2,
    xp: 40,
    tier: 5,
    pattern: 'aim',
    color: '#efe3c2',
    behaviour: 'charger',
    prefer: 8,
    style: 4,
    home: 'saltflat',
    lore: 'Half of a husk, twice as certain about where it is going.',
  },
  orrery: {
    name: 'Orrery Fragment',
    hp: 820,
    damage: 34,
    speed: 1.6,
    radius: 0.85,
    range: 22,
    rate: 2.6,
    xp: 215,
    tier: 6,
    pattern: 'spiral',
    color: '#b6c1de',
    behaviour: 'lantern',
    aura: { radius: 14, rate: 0.72 },
    style: 7,
    home: 'observatory',
    lore: 'One gear of a machine that counted stars. It still keeps time for everything near it.',
  },
  voidmoth: {
    name: 'Void Moth',
    hp: 900,
    damage: 40,
    speed: 3,
    radius: 0.9,
    range: 16,
    rate: 2.3,
    xp: 235,
    tier: 6,
    pattern: 'ring',
    color: '#8d93bb',
    behaviour: 'charger',
    prefer: 11,
    style: 10,
    home: 'observatory',
    lore: 'It was drawn to the observatory’s light. The light went out. It is still arriving.',
  },
  bogfiend: {
    name: 'Bog Fiend',
    hp: 1150,
    damage: 44,
    speed: 3.2,
    radius: 1,
    range: 15,
    rate: 2.2,
    xp: 290,
    tier: 6,
    pattern: 'burst',
    color: '#7d9273',
    behaviour: 'charger',
    prefer: 10,
    style: 8,
    home: 'marsh',
    lore: 'You hear the water move first. That is the only warning the marsh has ever given.',
  },
  marshlantern: {
    name: 'Marsh Lantern',
    hp: 980,
    damage: 38,
    speed: 1.4,
    radius: 0.8,
    range: 20,
    rate: 2.7,
    xp: 270,
    tier: 6,
    pattern: 'aim',
    color: '#c8d79a',
    behaviour: 'lantern',
    aura: { radius: 15, rate: 0.7 },
    style: 5,
    home: 'marsh',
    lore: 'It carries a light for the others. Nobody has ever seen what it looks like without one.',
  },
  glasshound: {
    name: 'Glass Hound',
    hp: 1450,
    damage: 48,
    speed: 3.4,
    radius: 0.95,
    range: 17,
    rate: 2.2,
    xp: 360,
    tier: 7,
    pattern: 'fan',
    color: '#b6dde5',
    behaviour: 'charger',
    prefer: 12,
    style: 6,
    home: 'glacier',
    lore: 'Fused mid-leap. Whatever it was chasing, it is still, technically, chasing it.',
  },
  prismshard: {
    name: 'Prism Shard',
    hp: 1300,
    damage: 44,
    speed: 2,
    radius: 0.95,
    range: 22,
    rate: 2.5,
    xp: 345,
    tier: 7,
    pattern: 'ring',
    color: '#c9ecf1',
    behaviour: 'splitter',
    splits: { kind: 'prismsliver', count: 2 },
    style: 9,
    home: 'glacier',
    lore: 'Break it and you get two of it. Break those and the glacier gets quieter for a while.',
  },
  prismsliver: {
    name: 'Prism Sliver',
    hp: 380,
    damage: 30,
    speed: 3,
    radius: 0.5,
    range: 16,
    rate: 2.1,
    xp: 80,
    tier: 7,
    pattern: 'ring',
    color: '#e0f5f8',
    behaviour: 'kiter',
    prefer: 13,
    style: 9,
    home: 'glacier',
    lore: 'Small enough to slip between two thoughts, and it usually does.',
  },
  ashanchor: {
    name: 'Ash Anchor',
    hp: 2600,
    damage: 52,
    speed: 0.8,
    radius: 1.5,
    range: 24,
    rate: 2.6,
    xp: 520,
    tier: 7,
    pattern: 'burst',
    color: '#c08a6d',
    behaviour: 'anchor',
    style: 8,
    home: 'ashfall',
    lore: 'The storm needs something to turn around. It volunteered, a long time ago.',
  },
  emberkite: {
    name: 'Emberkite',
    hp: 1700,
    damage: 50,
    speed: 3,
    radius: 0.85,
    range: 28,
    rate: 2.3,
    xp: 470,
    tier: 7,
    pattern: 'spiral',
    color: '#e8a271',
    behaviour: 'kiter',
    prefer: 23,
    style: 4,
    home: 'ashfall',
    lore: 'It rides the ash the way a gull rides a gale, and it has never once come down.',
  },
  tidechoir: {
    name: 'The Drowned Choir',
    hp: 9000,
    damage: 40,
    speed: 1.1,
    radius: 2.1,
    range: 30,
    rate: 1.7,
    xp: 1100,
    tier: 5,
    pattern: 'ring',
    color: '#8fd0cf',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    breakPoint: 0.014,
    style: 7,
    home: 'coast',
    lore: 'Forty voices that went under together and agreed to keep singing until someone answered.',
  },
  orchardmother: {
    name: 'The Petrified Mother',
    hp: 11000,
    damage: 42,
    speed: 0.9,
    radius: 2.2,
    range: 30,
    rate: 1.7,
    xp: 1300,
    tier: 5,
    pattern: 'fan',
    color: '#b6ac83',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    behaviour: 'summoner',
    summons: { kind: 'stonebark', count: 2, cooldown: 22 },
    breakPoint: 0.014,
    style: 5,
    home: 'orchard',
    lore: 'She stood over the last seedling until the stone reached her shoulders. It is still there.',
  },
  saltking: {
    name: 'The Salt King',
    hp: 14000,
    damage: 46,
    speed: 1.2,
    radius: 2.3,
    range: 34,
    rate: 1.6,
    xp: 1650,
    tier: 6,
    pattern: 'spiral',
    color: '#eee2c1',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    breakPoint: 0.013,
    style: 6,
    home: 'saltflat',
    lore: 'He was promised a kingdom of white towers. He got the flat, and he stayed anyway.',
  },
  orrerywarden: {
    name: 'The Broken Orrery',
    hp: 17000,
    damage: 48,
    speed: 1,
    radius: 2.4,
    range: 34,
    rate: 1.55,
    xp: 1950,
    tier: 6,
    pattern: 'spiral',
    color: '#a9b5d6',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    behaviour: 'lantern',
    aura: { radius: 20, rate: 0.75 },
    breakPoint: 0.013,
    style: 10,
    home: 'observatory',
    lore: 'It is still calculating. Nobody has told it the sky it was measuring is gone.',
  },
  marrowherald: {
    name: 'The Marrow Herald',
    hp: 21000,
    damage: 52,
    speed: 1.3,
    radius: 2.5,
    range: 32,
    rate: 1.5,
    xp: 2300,
    tier: 7,
    pattern: 'burst',
    color: '#94ab89',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    breakPoint: 0.012,
    style: 8,
    home: 'marsh',
    lore: 'It announces things. Nobody in the marsh has ever understood what, and nobody waits to ask.',
  },
  fusedtitan: {
    name: 'The Fused Titan',
    hp: 25000,
    damage: 55,
    speed: 1,
    radius: 2.6,
    range: 34,
    rate: 1.5,
    xp: 2700,
    tier: 7,
    pattern: 'fan',
    color: '#bfe6ee',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    breakPoint: 0.012,
    style: 9,
    home: 'glacier',
    lore: 'A hundred travelers caught in one wave of glass, and one shape that decided to stand up.',
  },
  stormremembers: {
    name: 'The Storm That Remembers',
    hp: 32000,
    damage: 60,
    speed: 1.1,
    radius: 2.8,
    range: 38,
    rate: 1.4,
    xp: 3400,
    tier: 8,
    pattern: 'burst',
    color: '#e0a181',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    breakPoint: 0.011,
    style: 8,
    home: 'ashfall',
    lore: 'The Crown has been holding this back since the beginning. It has had a long time to think.',
  },
  warrenmother: {
    name: 'The Warren Mother',
    hp: 2400,
    damage: 22,
    speed: 1.1,
    radius: 1.7,
    range: 24,
    rate: 1.9,
    xp: 260,
    tier: 1,
    pattern: 'fan',
    color: '#b4c489',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    behaviour: 'summoner',
    summons: { kind: 'thornling', count: 2, cooldown: 16 },
    breakPoint: 0.016,
    style: 5,
    lore: 'Every thorn in the orchard came from here, and she has never once left the room.',
  },
  lanternprime: {
    name: 'The First Lantern',
    hp: 3900,
    damage: 26,
    speed: 1.4,
    radius: 1.8,
    range: 26,
    rate: 1.8,
    xp: 400,
    tier: 2,
    pattern: 'spiral',
    color: '#9fe6de',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    behaviour: 'lantern',
    aura: { radius: 18, rate: 0.78 },
    breakPoint: 0.015,
    style: 7,
    lore: 'The light everything else was lit from. It has been waiting to be carried again.',
  },
  hivequeen: {
    name: 'The Hive Queen',
    hp: 6200,
    damage: 31,
    speed: 1.3,
    radius: 2,
    range: 28,
    rate: 1.7,
    xp: 620,
    tier: 3,
    pattern: 'ring',
    color: '#eec98d',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    behaviour: 'summoner',
    summons: { kind: 'scarab', count: 3, cooldown: 18 },
    breakPoint: 0.015,
    style: 6,
    lore: 'She built the glass. Every scarab in the waste is a letter she has not finished.',
  },
  vigilkeeper: {
    name: 'The Last Vigil',
    hp: 8800,
    damage: 36,
    speed: 1.1,
    radius: 2.1,
    range: 30,
    rate: 1.65,
    xp: 880,
    tier: 4,
    pattern: 'ring',
    color: '#c3a8dd',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    behaviour: 'bulwark',
    guard: 0.9,
    breakPoint: 0.014,
    style: 7,
    lore: 'Relieved at dawn, it was told. It has been counting dawns ever since.',
  },
  choirmaster: {
    name: 'The Cinder Choirmaster',
    hp: 12500,
    damage: 44,
    speed: 1.2,
    radius: 2.3,
    range: 32,
    rate: 1.6,
    xp: 1350,
    tier: 5,
    pattern: 'burst',
    color: '#f0a877',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    breakPoint: 0.013,
    style: 8,
    lore: 'It conducts the burning. Nobody has ever established who is singing.',
  },
  chapelwarden: {
    name: 'The Drowned Chaplain',
    hp: 14500,
    damage: 46,
    speed: 1,
    radius: 2.3,
    range: 32,
    rate: 1.6,
    xp: 1500,
    tier: 5,
    pattern: 'spiral',
    color: '#8fc9d6',
    boss: true,
    weak: { arc: 1.4, multiplier: 1.35 },
    breakPoint: 0.013,
    style: 9,
    lore: 'The service was never called off. The congregation simply stopped surfacing.',
  },
};
/**
 * The cost of the next level. Unchanged through twenty, so the first two hours are exactly
 * what they were, and then steeper: reaching thirty costs roughly three times what twenty
 * costs today, which is the length of the outer ring rather than a formality.
 */
export function xpForLevel(level: number) {
  const early = Math.round(50 + level * 24 + level * level * 4);
  return level <= 20 ? early : Math.round(2130 * Math.pow(1.115, level - 20));
}
/** The place at a point. Kept under its original name; `placeAt` is the full record. */
export const zoneAt = placeAt;
export function isSafe(p: Vec, dimension: Dimension) {
  return dimension === 'wilds' && Math.hypot(p.x - HAVEN.x, p.z - HAVEN.z) < HAVEN.radius;
}
export function distance(a: Vec, b: Vec) {
  return Math.hypot(a.x - b.x, a.z - b.z);
}
export const SHOP: {
  id: string;
  name: string;
  cost: number;
  description: string;
  slot?: Slot;
  icon: string;
}[] = [
  {
    id: 'potion',
    name: 'Hearth tonic',
    cost: 12,
    description: 'Restore 45% health. Carry up to 5.',
    icon: 'potion',
  },
  {
    id: 'weapon',
    name: 'Starforged weapon',
    cost: 90,
    description: 'Tier 2 Verdant weapon. +14 power.',
    slot: 'weapon',
    icon: 'sword',
  },
  {
    id: 'armor',
    name: 'Waykeeper’s mantle',
    cost: 75,
    description: 'Tier 2 Verdant armor. +70 health and damage reduction.',
    slot: 'armor',
    icon: 'armor',
  },
  {
    id: 'charm',
    name: 'A little remaining light',
    cost: 65,
    description: 'Tier 2 Verdant charm. +35 mana and faster recovery.',
    slot: 'charm',
    icon: 'spark',
  },
];
